import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { useI18n } from './I18nContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { t } = useI18n();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        // Standard unauthorized response when not logged in
        setUser(null);
        setIsAuthenticated(false);
      } else {
        console.warn(`Unexpected auth check status: ${response.status}`);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Auth check error:', error);
      }
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Manual setUser that also updates isAuthenticated
  const updateUser = (userData) => {
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
    } else {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const login = async () => {
    // Inside the native Android app, Google blocks OAuth in the embedded webview. So use the
    // OS-level native Google sign-in and hand the resulting ID token to the SAME /auth/session
    // endpoint the web uses — the backend is unchanged.
    if (Capacitor.isNativePlatform()) {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId) {
        console.error('Missing REACT_APP_GOOGLE_CLIENT_ID in the build.');
        return;
      }
      try {
        await SocialLogin.initialize({ google: { webClientId: clientId } });
        // No scopes on purpose: we only need authentication (the ID token, which already carries
        // email + profile). Passing scopes triggers the authorization flow, which the plugin
        // refuses unless the native MainActivity is modified — and we don't need Google APIs.
        const res = await SocialLogin.login({ provider: 'google', options: {} });
        const idToken = res?.result?.idToken;
        if (!idToken) {
          console.error('Google native login returned no idToken.');
          return;
        }
        await processSession(idToken);
      } catch (e) {
        // The user cancelling the account picker also lands here — nothing to surface.
        console.error('Native Google login error:', e);
      }
      return;
    }

    // Web: Google Identity Services (loaded lazily).
    if (!window.google) {

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => {

        initGoogleAuth();
      };
      document.body.appendChild(script);
    } else {
      initGoogleAuth();
    }
  };

  const initGoogleAuth = () => {
    if (!window.google) return;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;


    if (!clientId) {
      console.error('[GSI_LOGGER]: Missing REACT_APP_GOOGLE_CLIENT_ID in environment variables.');

      return;
    }


    window.google.accounts.id.initialize({
      client_id: clientId,
      use_fedcm_for_prompt: false, // Use traditional popup for better stability on localhost
      itp_support: true, // Enable Intelligent Tracking Prevention support
      callback: (response) => {
        if (response.credential) {

          processSession(response.credential);
        } else {
          console.error('Google login failed: No credential received');
        }
      },
    });

    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        const reason = notification.getNotDisplayedReason();

        if (reason === 'unregistered_origin') {
          console.error('CRITICAL: Origin mismatch! Ensure "' + window.location.origin + '" is in "Authorized JavaScript Origins" (NO trailing slash) in Google Console.');
        }
      } else if (notification.isSkippedMoment()) {

      } else if (notification.isDismissedMoment()) {

      } else {

      }
    });

    // Fallback: Open standard select account picker
    // We can also trigger this via a button if needed
  };

  const processSession = async (credential) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential }),
      });

      if (response.ok) {
        const userData = await response.json();

        setUser(userData);
        setIsAuthenticated(true);
        // Clear existing callback hash if any
        if (window.location.hash.includes('session_id')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Session processing error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (data) => {
    try {
      const response = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Profile update error:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser: updateUser,
      loading,
      isAuthenticated,
      login,
      logout,
      processSession,
      updateProfile,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth callback component
export function AuthCallback() {
  const { t } = useI18n();
  const { processSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef for the processed flag - set synchronously at start
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Get session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (sessionId) {
        const user = await processSession(sessionId);
        if (user) {
          // Clear the hash and navigate to dashboard
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { replace: true, state: { user } });
          return;
        }
      }

      // If no session_id or processing failed, go to login
      navigate('/login', { replace: true });
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-stone font-inter">{t('auth.processing')}</p>
      </div>
    </div>
  );
}

// Protected route wrapper
export function ProtectedRoute({ children }) {
  const { t } = useI18n();
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user was passed from AuthCallback, skip check
    if (location.state?.user) return;

    if (!loading && isAuthenticated === false) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone font-inter">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    return null;
  }

  return children;
}

// Admin route wrapper
export function AdminRoute({ children }) {
  const { t } = useI18n();
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/login', { replace: true });
      } else if (user?.role !== 'admin') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone font-inter">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'admin') {
    return null;
  }

  return children;
}

export default AuthContext;
